import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/components';

import { getLabelPerfReport } from '../../api/label-perf';
import { Header } from '../../components/Header';
import type { LabelPerfModel } from '../../types/label-perf';
import { formatNumberSuffix, getRelMonth } from '../../utils/format';
import { getTrendDirection } from '../../utils/trend';
import { Card, Title, Trend, Value } from './components';

export function LabelPerf() {
  const { t } = useTranslation('label-perf');

  const {
    data: labelPerfData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['label-perf'],
    queryFn: () => getLabelPerfReport([]),
  });

  const renderLoadingState = (): JSX.Element => (
    <p>
      <Icon type="spinner" variant="solid" size="lg" className="fa-spin" />
    </p>
  );
  const renderErrorState = (): JSX.Element => <p>{t('error.failedLoading')}</p>;

  const renderPerformanceCards = (data: LabelPerfModel): JSX.Element => (
    <div className="grid grid-cols-3">
      <Card className="bg-level-02 border-emphasis-subtle-right rounded-tl-sm rounded-bl-sm">
        <Title>{t('Total Assets')}</Title>
        <Value className="!text-display-5xl leading-none">
          {formatNumberSuffix(data.totalAssets)}
        </Value>
      </Card>

      <Card className="border-emphasis-subtle-right">
        <Title className="text-subtle">{t('Overall monthly Streams')}</Title>
        <div>
          <Trend
            direction={getTrendDirection(data.overallMonthlyStreamsChange)}
          >
            {formatNumberSuffix(data.overallMonthlyStreamsChange, 1, false)}
          </Trend>
          <span className="ml-1 text-[18px] font-normal">
            {getRelMonth(-1)} {t('vs.')} {getRelMonth(-2)}
          </span>
          <Value>{formatNumberSuffix(data.overallMonthlyStreams)}</Value>
        </div>
      </Card>

      <Card className="rounded-tr-sm rounded-br-sm">
        <Title className="text-subtle">{t('Overall monthly revenue')}</Title>
        <div>
          <Trend
            direction={getTrendDirection(data.overallMonthlyRevenueChange)}
          >
            {'$' +
              formatNumberSuffix(data.overallMonthlyRevenueChange, 1, false)}
          </Trend>
          <span className="ml-1 text-[18px] font-normal">
            {getRelMonth(-2)} {t('vs.')} {getRelMonth(-3)}
          </span>
          <Value>
            ${formatNumberSuffix(data.overallMonthlyRevenue)}
            <span>
              {' '}
              {t('in')} {getRelMonth(-3)}
            </span>
          </Value>
        </div>
      </Card>
    </div>
  );

  const renderOpportunityCards = (data: LabelPerfModel): JSX.Element => (
    <div className="grid grid-cols-3">
      <Card className="border-emphasis-subtle-right bg-primary-default rounded-tl-sm rounded-bl-sm">
        <Title className="text-white">
          {t('Yearly revenue opportunity from fixes')}
          <Icon
            type="info-circle"
            variant="solid"
            size="xs"
            className="ml-xs"
          />
        </Title>
        <Value>
          ${formatNumberSuffix(data.opportunityRevenueFromFixes)}
          <span className="text-base text-display-sm">/{t('year')}</span>
        </Value>
      </Card>

      <Card className="border-emphasis-subtle-right">
        <Title className="text-subtle">
          {t('Monthly streams increment from fixes')}
          <Icon
            type="info-circle"
            variant="solid"
            size="xs"
            className="ml-xs"
          />
        </Title>
        <div>
          <Trend direction={getTrendDirection(data.streamsFromFixesChange)}>
            {formatNumberSuffix(data.streamsFromFixesChange, 1, false)}
          </Trend>
          <span className="text-[18px] font-normal">
            {getRelMonth(-1)} {t('vs.')} {getRelMonth(-2)}
          </span>
          <Value>{formatNumberSuffix(data.streamsFromFixes)}</Value>
        </div>
      </Card>

      <Card className="rounded-tr-sm rounded-br-sm">
        <Title className="text-subtle">
          {t('Monthly revenue increment from fixes')}
          <Icon
            type="info-circle"
            variant="solid"
            size="xs"
            className="ml-xs"
          />
        </Title>
        <div>
          <Trend direction={getTrendDirection(data.revenueFromFixesChange)}>
            {'$' + formatNumberSuffix(data.revenueFromFixesChange, 1, false)}
          </Trend>
          <span className="text-[18px] font-normal">
            {getRelMonth(-2)} {t('vs.')} {getRelMonth(-3)}
          </span>
          <Value>${formatNumberSuffix(data.revenueFromFixes)}</Value>
        </div>
      </Card>
    </div>
  );

  return (
    <>
      <div className="flex justify-between items-center">
        <Header />
      </div>
      <div className="wrapper">
        {isLoading && renderLoadingState()}

        {isError && renderErrorState()}

        {labelPerfData && (
          <div>
            {/* Header Section */}
            <div className="flex justify-between mb-md items-end">
              <Title className="text-body-lg text-default">
                {t('Label performance')}
              </Title>
              <p className="text-body-md">
                <Icon type="info-circle" variant="solid" size="sm" />
                <span className="ml-xs">
                  {t('Stream count and dollar amounts are all')}{' '}
                  <span className="underline">{t('estimated')}</span>.
                </span>
              </p>
            </div>

            {/* Performance Cards */}
            <div className="rounded-sm border-emphasis-subtle overflow-hidden mb-2xl">
              {renderPerformanceCards(labelPerfData)}
            </div>

            {/* Opportunity Section */}
            <Title className="text-body-lg text-default mb-md">
              {t('Territory and DSP revenue opportunities')}
            </Title>
            <div className="rounded-sm border-emphasis-subtle overflow-hidden">
              {renderOpportunityCards(labelPerfData)}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

LabelPerf.displayName = 'LabelPerf';
